<template>
	<section class="order-block" :data-orderblok-id="orderBlock.id">

		<OrderBlockTitle :prefix="orderBlock.titlePrefix" :title="orderBlock.title" />

		<div v-if="orderBlock.description"
		     class="description"
		     v-html="descriptionHtml">
		</div>

		<div v-if="orderBlock.products && orderBlock.products.length > 0" class="products">

			<div class="products-group" :class="{'products-group--grid': orderBlockIsContractDuration}">
				<order-block-product
						v-for="product in productsAboveFold"
						:key="product.internalKey"
						:product="product" />
			</div>

			<ShowMoreAccordion
					v-if="productsBelowFold.length > 0"
					:show-less-text="orderBlock.collapse.collapseText"
					:show-more-text="orderBlock.collapse.expandText">

				<div class="products-group">
					<order-block-product
							v-for="product in productsBelowFold"
							:key="product.internalKey"
							:parent-id="orderBlock.id"
							:product="product" />
				</div>

				<div v-if="orderBlock.collapse.expandedFooterText"
				     class="footer-text"
				     v-html="orderBlock.collapse.expandedFooterText">
				</div>

			</ShowMoreAccordion>
		</div>

		<div v-if="orderBlock.footerDescription"
		     class="footer-description"
		     v-html="orderBlock.footerDescription">
		</div>

	</section>
</template>

<script lang="ts" setup>
	import OrderBlockProduct from '@/components/orderblock/OrderBlockProduct.vue';
	import OrderBlockTitle from '@/components/orderblock/OrderBlockTitle.vue';
	import ShowMoreAccordion from '@/components/orderblock/ShowMoreAccordion.vue';
	import {type OrderBlock, OrderBlockType} from '@/types/orderblock';
	import {computed} from 'vue';

	const props = defineProps<{
		orderBlock: OrderBlock,
	}>();

	const amountAboveFold = computed(() => props.orderBlock.collapse.maxProductAboveFold ?? null);

	const productsAboveFold = computed(() => {
		if (amountAboveFold.value === null || amountAboveFold.value === 0) {
			return props.orderBlock.products;
		}
		return props.orderBlock.products.slice(0, amountAboveFold.value);
	});

	const productsBelowFold = computed(() => {
		if (amountAboveFold.value === null || amountAboveFold.value === 0) {
			return [];
		}
		return props.orderBlock.products.slice(amountAboveFold.value);
	});

	const descriptionHtml = computed(() => {
		const description = props.orderBlock.description;
		if (description) {
			return description.replace(/√/g, '<span class=\'icon-check\'></span>');
		}
		return description;
	});

	const orderBlockIsContractDuration = computed(() => {
		return OrderBlockType.CONTRACT_DURATION === props.orderBlock.id;
	});
</script>

<style lang="scss" scoped>
	.products-group {
		margin: 2em 0;

		&:last-child {
			margin-bottom: 0;
		}

		&--grid {
			display: grid;
			grid-template-rows: repeat(auto-fit, minmax(1rem, 1fr));
			grid-template-columns: 1fr;
			grid-row-gap: 20px;
			grid-column-gap: 0;

			@media screen and (min-width: 768px) {
				display: grid;
				grid-template-columns: repeat(auto-fit, minmax(1rem, 1fr));
				grid-template-rows: 1fr;
				grid-column-gap: 20px;
				grid-row-gap: 0;
			}
		}
	}

	.description {
		margin: 0 0 2em;

		&:last-child {
			margin-bottom: 8px;
		}

		&:deep(ul) {
			padding-left: 1.2em;
		}

		&:deep(.icon-check) {
			background-image: url('@/assets/images/icon-check-green.svg');
			background-size: 1.6em 1.6em;
			background-position: -.26667em center;
			background-repeat: no-repeat;
			width: 1.5em;
			height: 1em;
			vertical-align: -.2em;
			display: inline-block;
		}
	}

	.footer-text {
		margin: 2em 0 0;
	}

	.footer-description {
		margin: 2em 0 8px;
	}
</style>
